/* global autosize */

import { Controller } from "stimulus";

// Drag-to-resize for a table column, applied to the column's `th`. Replaces the per-textarea
// resize handle, which looked per-row but resized the whole column.
export default class extends Controller {
  static values = { storageKey: String };

  connect() {
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onPointerCancel = this.onPointerCancel.bind(this);
    this.restoreWidth();
  }

  disconnect() {
    this.stopTracking();
  }

  startResize(event) {
    if (this.handle) return;

    event.preventDefault();
    this.startX = event.clientX;
    this.startWidth = this.element.getBoundingClientRect().width;
    // Cached for this drag only — rows can be added between drags.
    this.textareas = this.autosizedTextareas();

    // Capture so a release outside the window still ends the drag. Without it the listeners leak
    // and the next click anywhere resizes the column to wherever it landed.
    this.handle = event.currentTarget;
    this.pointerId = event.pointerId;
    this.handle.setPointerCapture(this.pointerId);
    this.handle.addEventListener("pointermove", this.onPointerMove);
    this.handle.addEventListener("pointerup", this.onPointerUp);
    this.handle.addEventListener("pointercancel", this.onPointerCancel);
    document.body.classList.add("is-column-resizing");
  }

  // Pointer events outpace the display, and every width change makes autosize remeasure each row,
  // so coalesce to one resize per frame.
  onPointerMove(event) {
    this.pointerX = event.clientX;
    if (this.frame) return;

    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      this.resizeTo(this.pointerX);
    });
  }

  onPointerUp(event) {
    this.resizeTo(event.clientX);
    this.stopTracking();
    this.persistWidth();
  }

  // The gesture was taken over — a touch turning into a scroll, or the browser interrupting.
  onPointerCancel() {
    this.applyWidth(this.startWidth);
    this.stopTracking();
  }

  stopTracking() {
    cancelAnimationFrame(this.frame);
    this.frame = null;
    this.textareas = null;
    document.body.classList.remove("is-column-resizing");

    if (!this.handle) return;

    if (this.handle.hasPointerCapture(this.pointerId)) this.handle.releasePointerCapture(this.pointerId);
    this.handle.removeEventListener("pointermove", this.onPointerMove);
    this.handle.removeEventListener("pointerup", this.onPointerUp);
    this.handle.removeEventListener("pointercancel", this.onPointerCancel);
    this.handle = null;
    this.pointerId = null;
  }

  resizeTo(clientX) {
    this.applyWidth(this.startWidth + (clientX - this.startX));
  }

  // No min/max of our own: the table's own min-content floor stops the column collapsing, and it
  // adapts to whichever columns are enabled. Only guard the CSS lower bound.
  applyWidth(width) {
    this.element.style.width = `${Math.max(0, Math.round(width))}px`;
    this.refreshAutosizedTextareas();
  }

  // A narrower column rewraps the text, so autosize has to recompute the row heights.
  refreshAutosizedTextareas() {
    const textareas = this.textareas ?? this.autosizedTextareas();

    if (textareas?.length) autosize.update(textareas);
  }

  autosizedTextareas() {
    return this.element.closest("table")?.querySelectorAll("textarea");
  }

  // Stored widths are untrusted input, so reject anything unparseable rather than writing it
  // into a style attribute (see the calendar_date.js comment for how that bites).
  restoreWidth() {
    if (!this.hasStorageKeyValue) return;

    const stored = Number.parseInt(window.localStorage.getItem(this.storageKeyValue), 10);
    if (Number.isNaN(stored)) return;

    this.applyWidth(stored);
  }

  persistWidth() {
    if (!this.hasStorageKeyValue) return;

    // The rendered width, so a value the browser floored is stored as what the user actually sees.
    window.localStorage.setItem(this.storageKeyValue, Math.round(this.element.getBoundingClientRect().width));
  }
}
